import { useState } from 'react';
import { MoneyInput, cn } from '@mlm/ui';

/**
 * Quantity control: − / + buttons around a TYPEABLE field, so ringing up 24 of
 * something isn't 23 taps. Sized for a finger: each button is a 44 px target
 * (`md`, the cart line) or 48 px (`lg`, the item sheet).
 *
 * While focused the field holds a local draft string rather than the committed
 * number — clearing it to type "12" must not snap back to 1 on the empty
 * keystroke (the bug in the member storefront's hand-rolled steppers, which all
 * do `Number(e.target.value) || 1` on every change). The draft commits, clamped
 * to `[1, cap]`, on blur and Enter; Escape restores the committed value.
 */
export function QtyStepper({
  qty,
  cap,
  onStep,
  onSet,
  size = 'md',
  minusDisabled = false,
}: {
  qty: number;
  cap: number;
  onStep: (delta: number) => void;
  onSet: (n: number) => void;
  size?: 'md' | 'lg';
  /** The item sheet cannot go below 1; a cart line's − at 1 removes the line. */
  minusDisabled?: boolean;
}) {
  const [draft, setDraft] = useState<string | null>(null);
  const lg = size === 'lg';

  function commit(raw: string) {
    setDraft(null);
    const n = Number.parseInt(raw, 10);
    // Empty / unparseable reverts to the committed qty; the clamp (incl. the
    // floor at 1) lives in `onSet` so − and + share it.
    if (Number.isNaN(n)) return;
    onSet(n);
  }

  const button = cn(
    'flex shrink-0 items-center justify-center font-medium leading-none text-foreground disabled:opacity-40',
    lg ? 'h-12 w-12 text-[22px]' : 'h-11 w-11 text-xl',
  );

  return (
    <span className="inline-flex items-center rounded-lg border border-input bg-background">
      <button
        type="button"
        aria-label="Decrease quantity"
        className={button}
        disabled={minusDisabled}
        onClick={() => onStep(-1)}
      >
        −
      </button>
      <MoneyInput
        integer
        // Sized by the stepper, not by the till-wide field rule (index.css).
        data-slot="qty-input"
        aria-label="Quantity"
        className={cn(
          'rounded-none border-0 bg-transparent px-0 py-0 text-center font-semibold focus-visible:ring-0 focus-visible:ring-offset-0',
          lg ? 'h-12 w-14 text-lg' : 'h-11 w-11 text-base',
        )}
        value={draft ?? String(qty)}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={(e) => e.currentTarget.select()}
        onBlur={(e) => commit(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          else if (e.key === 'Escape') {
            // Put the committed value back BEFORE blurring so the blur handler
            // commits a no-op — `setDraft(null)` alone wouldn't have landed yet.
            e.currentTarget.value = String(qty);
            e.currentTarget.blur();
          }
        }}
      />
      <button
        type="button"
        aria-label="Increase quantity"
        className={button}
        disabled={qty >= cap}
        onClick={() => onStep(+1)}
      >
        +
      </button>
    </span>
  );
}
